// Custom-wallpaper image store. The uploaded picture is downscaled in the browser
// and kept as a data: URL under WALLPAPER_KEY (its own localStorage key, with the
// generic cross-document store so the app + settings tabs stay in sync). Keeping
// it out of the settings blob means it never rides the BroadcastChannel on every
// tweak, never bloats settings export/import, and a quota failure here can't lose
// the rest of the user's settings.
import { WALLPAPER_KEY } from "./settings-schema.js";
import { readJSON, broadcast, remove, subscribe } from "./store.js";

// Cap the persisted string LENGTH (characters). localStorage holds UTF-16 (~2
// bytes/char) and the whole ~5 MB origin budget is shared with history /
// favorites / session, so we keep the wallpaper well under a third of it. The
// downscale ladder below targets this; if even the smallest attempt overflows we
// surface an error rather than risk a quota failure that could drop other data.
const MAX_CHARS = 1_400_000; // ~2.8 MB stored; leaves headroom in the 5 MB budget
const ATTEMPTS = [
	{ max: 2560, quality: 0.82 },
	{ max: 1920, quality: 0.8 },
	{ max: 1600, quality: 0.78 },
	{ max: 1280, quality: 0.72 },
];

export function getWallpaperImage() {
	const v = readJSON(WALLPAPER_KEY, "");
	return typeof v === "string" ? v : "";
}

export function clearWallpaperImage() {
	remove(WALLPAPER_KEY);
}

export function subscribeWallpaperImage(fn) {
	return subscribe(WALLPAPER_KEY, fn);
}

function loadImage(src) {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error("decode"));
		img.src = src;
	});
}

function readFileAsDataURL(file) {
	return new Promise((resolve, reject) => {
		const r = new FileReader();
		r.onload = () => resolve(String(r.result));
		r.onerror = () => reject(new Error("read"));
		r.readAsDataURL(file);
	});
}

// Draw onto a canvas capped at `max` on the long edge and encode to webp (with a
// JPEG fallback for browsers without webp canvas export). Returns a data: URL.
function encode(img, max, quality) {
	const scale = Math.min(1, max / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
	const w = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
	const h = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));
	const canvas = document.createElement("canvas");
	canvas.width = w;
	canvas.height = h;
	const ctx = canvas.getContext("2d");
	if (!ctx) return "";
	ctx.drawImage(img, 0, 0, w, h);
	let out = "";
	try { out = canvas.toDataURL("image/webp", quality); } catch (e) {}
	if (!out || out.indexOf("data:image/webp") !== 0) {
		try { out = canvas.toDataURL("image/jpeg", quality); } catch (e) { out = ""; }
	}
	return out;
}

// Accept a File, downscale/compress it under the size cap, and persist it.
// Resolves with the stored data URL; rejects with a user-facing Error message.
export async function setWallpaperImageFromFile(file) {
	if (!file || !/^image\//.test(file.type || "")) throw new Error("Please choose an image file.");
	const srcUrl = await readFileAsDataURL(file);
	let img;
	try { img = await loadImage(srcUrl); } catch (e) { throw new Error("That image couldn’t be read."); }

	let best = "";
	for (const a of ATTEMPTS) {
		const data = encode(img, a.max, a.quality);
		if (!data) continue;
		best = data;
		if (data.length <= MAX_CHARS) break;
	}
	if (!best) throw new Error("This browser can’t process that image.");
	if (best.length > MAX_CHARS) throw new Error("That image is too large even after shrinking. Try a smaller one.");

	// Write once (the store's writeJSON swallows quota errors, so we set it here to
	// detect a QuotaExceededError), then broadcast so subscribers + other tabs sync.
	try {
		localStorage.setItem(WALLPAPER_KEY, JSON.stringify(best));
	} catch (e) {
		throw new Error("Not enough storage space. Clear some data or use a smaller image.");
	}
	broadcast(WALLPAPER_KEY);
	return best;
}

// Store an already-encoded image data URL (e.g. restored from a shared look file).
// Validates the type + size cap; returns true on success, false if rejected.
export function setWallpaperImageData(dataUrl) {
	const s = String(dataUrl || "");
	// Anchor the WHOLE value to a strict base64 body (no quotes/parens/whitespace),
	// so a crafted look file can't smuggle a CSS-breaking payload into the url(...)
	// this data URL is later interpolated into (CSS value injection / external fetch).
	if (!/^data:image\/(png|jpe?g|webp|gif|avif|bmp);base64,[a-z0-9+/]+={0,2}$/i.test(s)) return false;
	if (s.length > MAX_CHARS) return false;
	try { localStorage.setItem(WALLPAPER_KEY, JSON.stringify(s)); } catch (e) { return false; }
	broadcast(WALLPAPER_KEY);
	return true;
}
